// Dependencies
import React, { useState } from 'react';

// eslint-disable-next-line lodash/import-scope
import { find, has, get } from 'lodash';
import { connect } from 'react-redux';

import { Impression } from '@nerdwallet/analytics/react/impression';
import { cdnUri } from '@nerdwallet/nw-client-lib/cdn';
import CityLoading from '../../shared/city-loading/city-loading';

// Local components
import getResultsHeaderConfig from '../../../../config/city-life/results-header-view-config';
import {
  fetchCityData,
  FETCH_COMPARE_CITY_DATA,
} from '../../../../lib/actions/fetchColData';
import {
  ENTITY_NAME,
  FORM_NAME,
  SECTION_NAME,
} from '../../../../lib/analytics-constants';
import { ACTIVE_TAB_CHANGE } from '../../../../reducers/cl/city-life';
import CityResultsError from '../../shared/city-results-error/city-results-error';
import ResultsHeader from '../../../currency/results-header/results-header';
import VerticalTabs from '../../../vertical-tabs/vertical-tabs';

import ClResultsBody from '../cl-results-body/cl-results-body';
import CompareTable from '../compare-table/compare-table';

// Config & Data
import { ClDataConfigProps } from '../../shared/types';

// CSS
require('./cl-results-panel.less');

// Constants
const imgUri = cdnUri('cola', 'jpg');

type ClResultsPanelProps = {
  data: ClDataConfigProps;
  dispatch: (any) => void;
  history: any;
  loading: boolean;
  location: any;
  customComponents: any;
  navBarConfig: any;
  onChange: (value: any, key: any) => void;
  howItComparesConfig: [];
};

const CLResultsPanel = ({
  data,
  dispatch,
  loading,
  customComponents,
  navBarConfig,
  onChange,
  howItComparesConfig,
}: ClResultsPanelProps): JSX.Element => {
  const [activeTitleIndex, setActiveTitleIndexCounter] = useState(-1);
  const [compareTitleIndex, setCompareTitleIndexName] = useState(-1);
  const [showCompare, setShowCompare] = useState(false);

  const onCompareClick = (compareTitleIndexInput) => {
    if (compareTitleIndex === -1) {
      // use this to figure out compareTitleIndex on page load, otherwise we'd have to hard code it's index
      // this is the "How does it compare?" index which would always = 4
      setCompareTitleIndexName(compareTitleIndexInput);
    }
    return (e?) => {
      if (e) {
        // the function was called from the onClick
        e.preventDefault();
        e.stopPropagation();
      }

      setShowCompare(true);

      // fetch complete city data for ALL city life cities
      dispatch({
        type: ACTIVE_TAB_CHANGE,
        activeTabIndex: compareTitleIndexInput,
      });

      if (!data.cities.every((x) => has(x, 'population'))) {
        // set active tab to compare tab
        dispatch(
          fetchCityData({
            ids: data.cities.map((city) => city.id),
            callback: (dataVal) => {
              dispatch({
                type: FETCH_COMPARE_CITY_DATA,
                data: dataVal,
              });
            },
          })
        );
      }

      if (typeof window !== 'undefined') {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    };
  };

  const onVerticalTabClick = (tabIndex) => {
    if (showCompare && tabIndex !== activeTitleIndex) {
      // switch back from compare page if user clicks tab that is not the compare tab (current tab)
      setShowCompare(false);
    } else if (!showCompare && tabIndex === compareTitleIndex) {
      // show compare page if we click the compareTitleIndex tab
      onCompareClick(tabIndex)();
    }

    setActiveTitleIndexCounter(tabIndex);
  };

  const { cities, totalNumCities, inputs, compareFieldConfig } = data;
  const cityData = find(cities, { id: inputs?.city });
  const config = getResultsHeaderConfig();

  const resultsHeaderProps = {
    config,
    customComponents,
    data,
    onChange,
  };

  if (loading) {
    return <CityLoading />;
  }

  if (!get(cityData, 'population')) {
    // server down and couldn't fetch data, so try to reload page
    const onRefreshClick = () => {
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    };

    return <CityResultsError onRefreshClick={onRefreshClick} />;
  }

  const colResultsBodyProps = {
    cityData,
    totalNumCities,
    dispatch,
    activeTitleIndex,
    activeTabIndex: data.activeTabIndex,
    onCompareClick,
  };

  const compareTableProps = {
    config: howItComparesConfig,
    compareFieldConfig,
    customComponents,
    data,
    onChange,
    currentCityId: cityData.id,
  };

  const verticalTabsConfig = {
    ...navBarConfig,
    headerIconUrl: imgUri(cityData.photo),
  };

  return (
    <div className="cl-results-panel">
      <Impression
        eventType="form_impression"
        entityName={ENTITY_NAME.CITY_LIFE_CITY_SEARCH}
        payload={{
          form_name: FORM_NAME.CITY_LIFE_CITY_SEARCH,
          section_name: SECTION_NAME.CITY_LIFE_CITY_SEARCH,
          section_position: 0,
          page_number: null,
          total_pages: null,
        }}
      >
        <ResultsHeader {...resultsHeaderProps} />
      </Impression>
      <div className="cl-results-wrapper">
        <VerticalTabs
          className="cl-nav-bar"
          trackingFormName="cl-nav-bar"
          impressionTracking
          onClick={onVerticalTabClick}
          activeTab={data.activeTabIndex}
          {...verticalTabsConfig}
        />
        {showCompare ? (
          <CompareTable {...compareTableProps} />
        ) : (
          <ClResultsBody {...colResultsBodyProps} />
        )}
      </div>
    </div>
  );
};

CLResultsPanel.displayName = 'CLResultsPanel';

const mapStateToProps = (state) => ({
  activeTabIndex: state?.activeTabIndex,
});
export default connect(mapStateToProps)(CLResultsPanel);
